import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';

import useDispatch from 'client/hooks/useDispatch';
import emptyFunction from 'common/util/emptyFunction';
import useSmartyAddressAutocompleteController, {
  STATUS_HAS_RESULTS,
  STATUS_LOADING,
  STATUS_NO_RESULTS,
  STATUS_TYPING,
} from './useSmartyAddressAutocompleteController';
import './SrFormFieldSmartyAddressAutoComplete.less';

const SuggestionItem = ({ description, entriesCount }) => {
  return (
    <div>
      <div styleName="suggestionDescription">{description}</div>
      {entriesCount > 1 && (
        <div styleName="suggestionMetadata">Requires Unit • {entriesCount} Options</div>
      )}
    </div>
  );
};

const SrFormFieldSmartyAddressAutoComplete = ({
  value,
  formField,
  onChange,
  textInputFieldAttributes = {},
  onBlur,
  onSelectAddress,
  onManualEntry,
}) => {
  const dispatch = useDispatch();
  const [isRefiningSearch, setIsRefiningSearch] = useState(false);

  const LOADING_TEXT = 'Loading...';

  const {
    suggestions,
    selectedAddress,
    status,
    lastFetchedValue,
    cancelDebounced,
    clearSuggestions,
    handleInputValueChange,
    handleAddressRefinement: controllerHandleAddressRefinement,
    handleSingleAddressSelection: controllerHandleSingleAddressSelection,
  } = useSmartyAddressAutocompleteController({ dispatch, formFieldId: formField.id });

  const handleSuggestionSelection = (event, { suggestion }) => {
    const isMultiUnit = suggestion.entriesCount > 1;
    if (isMultiUnit) {
      controllerHandleAddressRefinement(suggestion, { onChange, setIsRefiningSearch });
    } else {
      controllerHandleSingleAddressSelection(suggestion, { onChange, onSelectAddress, onBlur });
    }
    cancelDebounced();
    clearSuggestions();
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur(selectedAddress);
    }
  };

  const handleKeyDownCapture = (event) => {
    if (
      suggestions.length > 0 &&
      (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter')
    ) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <div onKeyDownCapture={handleKeyDownCapture}>
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsClearRequested={emptyFunction}
        onSuggestionsFetchRequested={emptyFunction}
        getSuggestionValue={(suggestion) => suggestion.description}
        renderSuggestion={(suggestion) => (
          <SuggestionItem
            description={suggestion.description}
            entriesCount={suggestion.entriesCount}
          />
        )}
        shouldRenderSuggestions={() => status === STATUS_HAS_RESULTS}
        onSuggestionSelected={handleSuggestionSelection}
        highlightFirstSuggestion={false}
        inputProps={{
          ...textInputFieldAttributes,
          autoComplete: 'off',
          'data-form-type': 'other',
          'data-1p-ignore': true,
          'data-lpignore': true,
          'data-bwignore': true,
          value: value || '',
          onChange: (event, { newValue }) =>
            handleInputValueChange(newValue, { onChange, onSelectAddress, isRefiningSearch }),
          onBlur: handleBlur,
        }}
      />
      {(status === STATUS_TYPING || status === STATUS_LOADING) && (
        <div styleName="container">
          <div styleName="statusContainer">
            <span styleName="statusText">{LOADING_TEXT}</span>
          </div>
        </div>
      )}
      {status === STATUS_NO_RESULTS && (
        <div styleName="container">
          <div styleName="noResultsContainer">
            <span styleName="noResultsText">No addresses found matching "{lastFetchedValue}"</span>
            <button type="button" styleName="manualAddButton" onClick={onManualEntry}>
              + Add address manually
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

SrFormFieldSmartyAddressAutoComplete.propTypes = {
  value: PropTypes.string,
  formField: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  textInputFieldAttributes: PropTypes.object,
  onBlur: PropTypes.func,
  onSelectAddress: PropTypes.func,
  onManualEntry: PropTypes.func,
};

export default SrFormFieldSmartyAddressAutoComplete;
